import { useEffect, useMemo, useState, type ReactElement } from "react";
import EmptyState from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { useParams } from "react-router-dom";
import { ChartLineIcon, Notebook, User } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useShareStore, ShareTab } from "@/stores/shareStore";
import { useCreatorsStore } from "@/stores/creators/creatorsStore";
import { useAnalyticsStore } from "@/stores/creators/analyticsStore";
import { useCampaignStore } from "@/stores";
import { Creator, Campaign } from "@/types/types";
import ShareAnalyticsBody from "@/components/share/ShareAnalyticsBody";
import ShareBriefBody from "@/components/share/ShareBriefBody";
import ShareCreatorsBody from "@/components/share/ShareCreatorsBody";

const TAB_META: Record<ShareTab, { label: string; icon: ReactElement }> = {
  creators: {
    label: "Creators",
    icon: <User className="-ms-0.5 me-1.5 opacity-60" size={16} aria-hidden="true" />,
  },
  brief: {
    label: "Brief",
    icon: <Notebook className="-ms-0.5 me-1.5 opacity-60" size={16} aria-hidden="true" />,
  },
  analytics: {
    label: "Analytics",
    icon: <ChartLineIcon className="-ms-0.5 me-1.5 opacity-60" size={16} aria-hidden="true" />,
  },
};

/**
 * Public, login-less read-only campaign page rendered at /share/:token (and at
 * the root of the share.* subdomain). Fetches the sanitized public payload,
 * hydrates the stores the reused components read from, and renders the enabled
 * sections.
 */
export default function SharePage() {
  const { token } = useParams<{ token: string }>();
  const { loading, notFound, data, fetchShare } = useShareStore();
  const [activeTab, setActiveTab] = useState<ShareTab | null>(null);

  useEffect(() => {
    if (token) fetchShare(token);
  }, [token, fetchShare]);

  // Map the sanitized payload into Creator objects (with a synthetic activated/
  // pending status) so the reused analytics + roster components work unchanged.
  const creators: Creator[] = useMemo(() => {
    return (data?.creators ?? []).map(
      (c: any) =>
        ({
          _id: c._id,
          username: c.username,
          avatar: c.avatar,
          platform: c.platform,
          platform_id: c.platform_id,
          country: c.country,
          followers: c.followers,
          average_views: c.average_views,
          median_views: c.median_views,
          engagement_rate: c.engagement_rate,
          uploaded_links: c.uploaded_links ?? [],
          fields: c.fields ?? [],
          // has_thread (when conversations are shared) drives the inbox button.
          thread_id: c.has_thread ? c._id : undefined,
          // Real status when exposed (show_fields on), else synthetic from is_post_activation.
          status: c.status ?? (c.is_post_activation ? "activated" : "pending"),
          // Required-by-type defaults; never surfaced in read-only UI.
          email: "",
          autopilot: false,
          hand_off_to_user: false,
          unanswered_question: "",
          debug_info: "",
        } as Creator)
    );
  }, [data?.creators]);

  useEffect(() => {
    if (!data) return;
    useCreatorsStore.getState().setCreators(creators);
    useAnalyticsStore.setState({
      campaignAnalyticsData: data.analytics?.results ?? [],
      selectedTab: data.campaign.use_dub ? "conversion" : "video",
      isLoading: false,
    });
    useCampaignStore.setState({
      campaign: {
        campaign_name: data.campaign.campaign_name,
        platform: data.campaign.platform,
        use_dub: data.campaign.use_dub,
        // Field defs drive the dynamic creator columns when data sharing is on.
        fields: data.campaignFields ?? [],
        // Stage defs let the status badge resolve stage-UUID statuses.
        stages: data.campaign.stages ?? [],
      } as unknown as Campaign,
      campaignId: null,
    });
  }, [data, creators]);

  useEffect(() => {
    if (data && !activeTab && data.tabs.length) setActiveTab(data.tabs[0]);
  }, [data, activeTab]);

  if (loading || (!data && !notFound)) {
    return (
      <div className="w-full h-dvh flex items-center justify-center">
        <Spinner className="size-5 text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="relative w-full h-dvh flex flex-col items-center justify-center gap-6 text-center px-4 overflow-hidden">
        <div className="relative flex flex-col items-center">
          {/* Amy peeks up over the horizon line with a little speech bubble. */}
          <div className="relative">
            <div className="absolute -top-4 left-full -translate-x-2 whitespace-nowrap rounded-2xl rounded-bl-sm border bg-background px-3 py-1.5 text-xs font-medium text-foreground/90 shadow-sm">
              Hmm, this one's not here 🤔
            </div>
            <img
              src="/amy.svg"
              alt="Amy"
              draggable={false}
              className="size-36 object-contain select-none drop-shadow-sm"
            />
          </div>
        </div>

        <div className="flex flex-col gap-2 max-w-sm">
          <h1 className="text-2xl font-semibold">This link went missing</h1>
          <p className="text-muted-foreground text-sm">
            It either never existed or has been revoked. Try asking whoever sent
            it for a fresh one.
          </p>
        </div>
      </div>
    );
  }

  const tabs = data.tabs;
  if (!tabs.length) {
    return (
      <div className="w-full h-dvh flex items-center justify-center px-4">
        <EmptyState text="No sections are enabled for this share link" />
      </div>
    );
  }
  const current = activeTab && tabs.includes(activeTab) ? activeTab : tabs[0];
  return (
    <div className="flex flex-col w-full h-dvh">
      <div className="flex items-center min-h-12 justify-between w-full border-b px-4 bg-sidebar gap-3">
        <h1 className="text-lg font-semibold truncate">
          {data.campaign.campaign_name}
        </h1>
        {tabs.length > 1 && (
          <Tabs
            value={current}
            onValueChange={(v) => setActiveTab(v as ShareTab)}
          >
            <TabsList variant="underline">
              {tabs.map((t) => (
                <TabsTrigger key={t} value={t}>
                  <span className="lg:block hidden">{TAB_META[t].icon}</span>
                  {TAB_META[t].label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        )}
      </div>

      <div className="h-full w-full overflow-auto">
        {current === "analytics" && (
          <ShareAnalyticsBody
            results={data.analytics?.results ?? []}
            creators={creators}
            useDub={data.campaign.use_dub}
          />
        )}
        {current === "brief" && <ShareBriefBody brief={data.brief ?? {}} />}
        {current === "creators" && (
          <ShareCreatorsBody
            creators={creators}
            options={data.creatorOptions}
            token={token ?? ""}
          />
        )}
      </div>
    </div>
  );
}
